import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/NewFiles/Css/ProjectsDashboard.css";
import api from "../axios/api";
import { FaPlus, FaPen, FaTrash } from "react-icons/fa";
import {
  Box,
  Switch,
  useMediaQuery,
  Typography,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Button from "../components/common/styled components/Button";
import CommonGrid from "../components/common/Grid";
import CloseIcon from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";

const SpeciesMaster = () => {
  const navigate = useNavigate();
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch species data
  const fetchSpecies = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      const response = await api.get("/api/admin/getSpecies", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status) {
        setData(response.data.data || []);
        setFilteredProjects(response.data.data || []);
      } else {
        console.error("Failed to load species");
        toast.error("Failed to load species data");
      }
    } catch (error) {
      console.error("Error fetching species:", error);
      toast.error("Error fetching species data");
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, []);

  const handleEdit = (item) => {
    navigate(`/dashboard/myspecies/edit/${item.id}`, { state: { species: item } });
  };

  const handleAdd = () => {
    navigate("/dashboard/myspecies/add");
  };

  // Delete species data
  const handleDelete = async () => {
    setIsDelete(true);
    const token = localStorage.getItem("token");
    try {
      const response = await api.delete(
        `/api/admin/deleteSpecies/${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status) {
        toast.success("Species deleted successfully!");
        await fetchSpecies();
      } else {
        toast.error("Failed to delete species.");
      }
    } catch (error) {
      console.error("Delete failed:", error.response?.data || error);
      toast.error("Error occurred while deleting species.");
    } finally {
      setIsDelete(false);
      setDeleteModal(false);
      setDeleteId(null);
    }
  };

  // Change Species Status
  const handleStatusChange = async (id, newIsActive) => {
    const token = localStorage.getItem("token");

    try {
      const response = await api.put(
        `/api/admin/updateSpeciesStatus/${id}`,
        { isActive: newIsActive },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status) {
        toast.success(`Species ${newIsActive ? "Activated" : "Deactivated"}`);
        await fetchSpecies();
      } else {
        toast.error("Failed to update status.");
      }
    } catch (error) {
      console.error("Status update failed:", error.response?.data || error);
      toast.error("Error occurred while updating status.");
    }
  };

  useEffect(() => {
    fetchSpecies();
  }, [fetchSpecies]);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setDeleteId(null);
  }, []);

  return (
    <div>
      <div
        className="projects-dashboard"
        style={{ padding: isMobile ? "1rem" : "2rem" }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        <div
          className={isLoading ? "content-disabled" : ""}
          style={{
            pointerEvents: isLoading ? "none" : "auto",
            opacity: isLoading ? 0.6 : 1,
            transition: "opacity 0.3s",
          }}
        >
          {/* Header section with create button */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              mb: 3,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 4,
                  height: 28,
                  bgcolor: "#10b981",
                  borderRadius: "4px",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontFamily: "'Plus Jakarta Sans', 'Outfit', 'Inter', sans-serif",
                  fontSize: { xs: "1.4rem", sm: "1.75rem" },
                  fontWeight: 700,
                  color: "#002F43",
                  letterSpacing: "-0.02em",
                  m: 0,
                  lineHeight: 1.2,
                }}
              >
                Species Master
              </Typography>
            </Box>

            <Button
              onClick={handleAdd}
            >
              <FaPlus size="14px" style={{ marginRight: "8px" }} />
              Add New Species
            </Button>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteModal}
              onClose={handleDeleteModalClose}
              maxWidth="sm"
            >
              <DialogTitle>
                Confirm Delete
                <IconButton
                  onClick={handleDeleteModalClose}
                  style={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                Are you sure you want to delete this species? This action cannot
                be undone.
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteModalClose} variant="cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={isDelete}
                  variant="contained"
                  color="danger"
                >
                  {isDelete ? "Deleting..." : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>

          {/* Data Table with CommonGrid */}
          <CommonGrid
            columns={[
              {
                header: "Image",
                width: "80px",
                align: "center",
                render: (_, item) =>
                  item.icon_url ? (
                    <Box
                      component="img"
                      src={item.icon_url}
                      alt={item.label || "Species Icon"}
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "6px",
                        objectFit: "cover",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        display: "inline-block",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No Image
                    </Typography>
                  ),
              },
              {
                header: "Label (Scientific)",
                accessor: "label",
                sortable: true,
                align: "left",
                render: (val) => (
                  <span style={{ fontWeight: 600, color: "#0f172a" }}>{val}</span>
                ),
              },
              {
                header: "Common Name",
                accessor: "common_name",
                sortable: true,
                align: "left",
                render: (val) => (
                  <span style={{ color: "#334155" }}>{val}</span>
                ),
              },
              {
                header: "Form Factor",
                accessor: "form_factor",
                sortable: true,
                align: "center",
                render: (val) => (val != null ? Number(val).toFixed(2) : "0.00"),
              },
              {
                header: "Biomass Density",
                accessor: "bd_biomass_density",
                sortable: true,
                align: "center",
                render: (val) => (val != null ? Number(val).toFixed(2) : "0.00"),
              },
              {
                header: "Sequence",
                accessor: "sequence",
                sortable: true,
                align: "center",
                render: (val) => val ?? "-",
              },
              {
                header: "References",
                accessor: "references",
                align: "left",
                render: (val) => {
                  const plainText = val
                    ? val.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
                    : "";
                  if (!plainText) {
                    return <span style={{ color: "#94a3b8" }}>-</span>;
                  }
                  return (
                    <span
                      title={plainText}
                      style={{
                        display: "inline-block",
                        maxWidth: "180px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "help",
                        color: "#334155",
                        verticalAlign: "middle",
                      }}
                    >
                      {plainText}
                    </span>
                  );
                },
              },
              {
                header: "Status",
                align: "center",
                render: (_, item) => (
                  <Switch
                    checked={item.isActive === 1}
                    onChange={() =>
                      handleStatusChange(
                        item.id,
                        item.isActive === 1 ? 0 : 1
                      )
                    }
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "green",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "green",
                          opacity: 1,
                        },
                      },
                      "& .MuiSwitch-switchBase": {
                        color: "red",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "red",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                ),
              },
              {
                header: "Actions",
                align: "center",
                render: (_, item) => (
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(item)}
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#e0f7fa" },
                      }}
                      title="Edit Species"
                    >
                      <FaPen color="#1976d2" size={13} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeleteId(item.id);
                        setDeleteModal(true);
                      }}
                      sx={{
                        bgcolor: "#f5f5f5",
                        "&:hover": { bgcolor: "#ffebee" },
                      }}
                      title="Delete Species"
                    >
                      <FaTrash color="#d32f2f" size={13} />
                    </IconButton>
                  </Box>
                ),
              },
            ]}
            data={filteredProjects}
            loading={loading}
            variant="navy"
            pagination={true}
            pageSize={10}
            pageSizeOptions={[5, 10, 20, 50]}
            rowKey="id"
          />
        </div>
      </div>
    </div>
  );
};

export default SpeciesMaster;
